import React from "react";
import { connect } from "react-redux";
import styled, { withTheme } from "styled-components";
import { selectors as ancillarySelectors } from "../../../../../../redux/ancillaries";
import { Button } from "@vitalsource/vstui";
import { t } from "../../../../../core/translate";
import { cloud } from "@vitalsource/vstui/colors";
import { toastUtils } from "../../../../../../utils/toast-utils";

import Register from "../../../icons/courseware/register";
import Reveal from "../../../icons/courseware/reveal";
import Visit from "../../../icons/courseware/visit";
import { actions as uiLibraryActions } from "../../../../../../redux/ui/library";
import { actions as modalActions } from "../../../../../../redux/modals";
import { selectors as booksSelectors } from "../../../../../../redux/books";
import { OffScreenStyles } from "../../../../../components/mixins";

const CoursewareInfo = styled.div`
  height: 100%;
`;

const ScrollingContainer = styled.div`
  overflow: auto;
  height: calc(100% - ${props => (props.isRevealed ? "100px" : "0px")});
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  top: 15px;
`;

const Instructions = styled.div`
  color: ${cloud.getShade(9)};
  background: #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 20px;
  flex-grow: 0;
  flex-shrink: 0;
  font-size: 1.6rem;
  span:first-of-type {
    padding-bottom: 10px;
  }
  span {
    font-weight: 700;
    display: flex;
    align-items: center;
  }
  svg {
    margin-right: 18px;
  }
`;

const ProductIsbn = styled.span`
  font-weight: 700;
  font-size: 1.4rem;
  color: #000000;
`;

const Warning = styled.div`
  margin: 15px auto 25px auto;
  color: ${cloud.getShade(9)};
`;

const VerticalDash = styled.div`
  width: 2px;
  height: 24px;
  margin: 3px 0px 3px 30px;
  background-color: #fead64;
`;

const PanelFooter = styled.div`
  padding-right: 30px;
  background-color: ${cloud.getShade(1)};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  min-height: 88px;
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
`;

const Note = styled.span`
  position: relative;
  top: -14px;
  font-style: italic;
  font-size: 1.2rem;
  letter-spacing: 0;
  margin-left: 100px;
`;

const AccessCodeContainer = styled.div`
  margin: 0 auto 25px auto;
  width: 365px;
`;

const LaunchCoursewareContainer = styled(AccessCodeContainer)``;
const RevealCodeContainer = styled(AccessCodeContainer)``;

const Header = styled.h3`
  margin: 0 0 10px 0;
`;

const AccessCodeCopyURL = styled.div`
  background: #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;

const Text = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${cloud.getShade(3)};
`;

const PanelHeadingTag = styled.h3`
  ${OffScreenStyles};
`;

class BookCoursewareContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  renderRevealedCoursewareContent() {
    const { accessCode, theme } = this.props;
    return (
      <div>
        <PanelHeadingTag>
          {t("eresources.courseware", "Courseware")}
        </PanelHeadingTag>
        {accessCode.accessCode && (
          <AccessCodeContainer>
            <Header>{t("eresources.access-code")}</Header>
            <AccessCodeCopyURL>
              <Text>{accessCode.accessCode}</Text>
              {Fraction.Helpers.copyEnabled() && (
                <Button
                  label={t("library.copy-link")}
                  fullWidth
                  color="white"
                  colorBehindButton={theme.color.primary.getShade(7)}
                  tooltipPlacement="bottom"
                  onClick={this.copyAccessCode}
                >
                  {t("reader.notebook.copy")}
                </Button>
              )}
            </AccessCodeCopyURL>
          </AccessCodeContainer>
        )}
        {accessCode.href && (
          <LaunchCoursewareContainer>
            <Header>{t("library.url")}</Header>
            <AccessCodeCopyURL>
              <Text>{accessCode.href}</Text>
              <Button
                label={t("eresources.launch-courseware")}
                fullWidth
                color="white"
                colorBehindButton={theme.color.primary.getShade(7)}
                tooltipPlacement="bottom"
                href={accessCode.href}
                target="blank"
              >
                {t("eresources.launch-courseware")}
              </Button>
            </AccessCodeCopyURL>
          </LaunchCoursewareContainer>
        )}
      </div>
    );
  }

  copyAccessCode = () => {
    this.copyCodeAndNotify(
      this.props.accessCode.accessCode,
      t("eresources.access-code-copied")
    );
  };

  copyCodeAndNotify(text, success) {
    const result = Fraction.Helpers.copyText(text);
    if (result) {
      toastUtils.notify(success, { type: "success" });
    } else {
      toastUtils.notify(t("errors.copy-failed"), { type: "error" });
    }
  }

  renderCoursewareInstructions() {
    const { isbn, closePanel } = this.props;

    return (
      <RevealCodeContainer>
        <Header>
          {t(
            "eresources.reveal-header",
            "Before this product is accessible you’ll need to…"
          )}
        </Header>
        <Instructions>
          <span>
            <Reveal />
            {t("eresources.reveal-1", "1. Reveal your Access Code")}
          </span>
          <VerticalDash />
          <span>
            <Visit />
            {t("eresources.reveal-2", "2. Visit the Courseware Website")}
          </span>
          <VerticalDash />
          <span>
            <Register />
            {t("eresources.reveal-3", "3. Register with your Access Code")}
          </span>
          <Note>
            {t(
              "eresources.reveal-process-2",
              "A Course Key may also be required, this could be found on a receipt or obtained from an instructor."
            )}
          </Note>
        </Instructions>
        <Warning>
          {window.toHTML(
            t(
              "eresources.reveal-process-info",
              `Courseware is often assigned by an instructor. This product’s ISBN is${" "}
          ${(
            <ProductIsbn>{isbn}</ProductIsbn>
          )}, make sure this is what’s intended
          before continuing.`,
              { isbn }
            )
          )}
        </Warning>
        <PanelFooter>
          <Button onClick={closePanel} naked>
            Cancel
          </Button>
          <Button
            onClick={() => {
              this.props.closePanel();
              this.props.openModal("revealCodeModal", { isbn });
            }}
          >
            {t("base.continue", "Continue")}
          </Button>
        </PanelFooter>
      </RevealCodeContainer>
    );
  }

  render() {
    const { accessCode, book } = this.props;
    const codeRevealed = accessCode.revealed;

    if (!book.ssoOnly) {
      return (
        <CoursewareInfo isRevealed={!codeRevealed}>
          <ScrollingContainer>
            {codeRevealed && this.renderRevealedCoursewareContent()}
            {!codeRevealed && this.renderCoursewareInstructions()}
          </ScrollingContainer>
        </CoursewareInfo>
      );
    }
    return null;
  }
}

const mapStateToProps = (state, props) => ({
  accessCode: ancillarySelectors.getAccessCodesForVbid(state, props.isbn),
  book: booksSelectors.getBookByIsbn(state, props.isbn)
});

const mapDispatchToProps = dispatch => ({
  openModal: (type, props) => dispatch(modalActions.openModal(type, props)),
  closePanel: () => dispatch(uiLibraryActions.closePanel())
});

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(BookCoursewareContainer)
);
